import { Box3, Euler, OrthographicCamera, Quaternion, Vector3 } from 'three'

export const DICE_TUMBLE_MS = 1160
export const ROLL_PRESENTATION_MS = 1760
export const REDUCED_ROLL_MS = 800
export const DICE_IVORY = '#fff8e7'
export const DICE_PIP = '#173e43'
export const DICE_FACE_VALUES = [3,4,1,6,2,5]
// Mac board spacing is 1.65; the web board uses 2 world units per space.
const macScale=2/1.65
export const DICE_SIZE=.66*1.30*macScale
export const BOARD_SURFACE=.48
const restingRotations = [[0,0,0],[-Math.PI/2,0,0],[0,0,Math.PI/2],[0,0,-Math.PI/2],[Math.PI/2,0,0],[Math.PI,0,0]]
export type DiceLanding={position:Vector3;yaw:number;start:Vector3;spin:Vector3}
export function boardDiceLandings(tile:Vector3,random= Math.random):DiceLanding[]{
  const firstYaw=(random()*2-1)*Math.PI
  return [0,1].map(i=>{
    const position=new Vector3(tile.x*.60+((i===0?-.8:.8)+(random()*.2-.1))*macScale,BOARD_SURFACE+DICE_SIZE/2,tile.z*.60+(random()*.56-.28)*macScale)
    return {position,yaw:firstYaw+(i===0?0:Math.PI/12+random()*Math.PI/4),start:new Vector3(position.x-(1.4+random()*.6)*macScale,(4.8+random()*.8)*macScale,position.z-(2+random()*.6)*macScale),spin:new Vector3((3+random()*2)*Math.PI,(1+random()*2)*Math.PI,(1+random()*2)*Math.PI)}
  })
}
export function boardDicePose(landing:DiceLanding,value:number,index:number,elapsed:number,reduced:boolean){
  const r=restingRotations[value-1],endRotation=new Quaternion().setFromEuler(new Euler(r[0],landing.yaw,r[2],'YXZ'))
  if(reduced||elapsed<0||elapsed>=DICE_TUMBLE_MS/1000)return {position:landing.position.clone(),rotation:endRotation}
  const t=Math.max(0,elapsed-index*.06),end=landing.position,position=end.clone()
  const at=(dx:number,dy:number,dz:number)=>end.clone().add(new Vector3(dx,dy,dz).multiplyScalar(macScale))
  if(t<.46)position.copy(landing.start).lerp(at(-.4,0,-.3),(t/.46)**2)
  else if(t<.62)position.copy(at(-.4,0,-.3)).lerp(at(-.15,.55,-.1),(t-.46)/.16)
  else if(t<.78)position.copy(at(-.15,.55,-.1)).lerp(end,(t-.62)/.16)
  else if(t<.85)position.copy(at(0,.18*(t-.78)/.07,0))
  else if(t<.92)position.copy(at(0,.18*(1-(t-.85)/.07),0))
  const spin=landing.spin.clone().multiplyScalar(Math.min(t,.92)/.92)
  const rotation=new Quaternion().setFromEuler(new Euler(spin.x,spin.y,spin.z))
  if(t>.92)rotation.slerp(endRotation,Math.min(1,(t-.92)/.18))
  // Keep all corners above the board even while the cubes are tumbling.
  const up=new Vector3(0,1,0).applyQuaternion(rotation.clone().invert())
  position.y=Math.max(position.y,BOARD_SURFACE+DICE_SIZE/2*(Math.abs(up.x)+Math.abs(up.y)+Math.abs(up.z)))
  return {position,rotation}
}

// Frame the actual board dice, using the Mac viewing direction. No second scene,
// overlay dice, tray, or depth-buffer override: these cubes belong to the city.
export function frameBoardRollCamera(camera:OrthographicCamera,width:number,height:number,landings:DiceLanding[],controlsBottom=235){
  const bounds=new Box3()
  for(const l of landings){bounds.expandByPoint(l.start);bounds.expandByPoint(l.position)}
  bounds.expandByScalar(DICE_SIZE*.9)
  const target=bounds.getCenter(new Vector3())
  camera.position.copy(target).add(new Vector3(14,28,22));camera.lookAt(target);camera.updateMatrixWorld()
  const viewBounds=bounds.clone().applyMatrix4(camera.matrixWorldInverse),size=viewBounds.getSize(new Vector3())
  const bottom=controlsBottom,top=Math.min(108,Math.max(24,height-bottom-100)),available=Math.max(40,height-top-bottom)
  const span=Math.max(14,size.y*height/available*1.08,size.x*height/(width-44)*1.08)
  const centerY=top+available/2,offset=(1-2*centerY/height)*span/2
  camera.left=-span*width/height/2;camera.right=-camera.left;camera.top=span/2-offset;camera.bottom=-span/2-offset;camera.updateProjectionMatrix()
  return target
}
